<template>
  <div v-loading="loading" v-if="diagToDisplay" class="mw-container">
    <div class="mb-2">
      <NavigationBreadcrumb v-if="!isPreview" :bc-path="diagPath" />
    </div>
    <div
      v-if="!hasEmptyBody"
      :class="[{ preview: isPreview }, 'mw-diag-content', 'mt-2']"
    >
      <NodeContentViewer :body="diagToDisplay.body" />
    </div>
    <div
      v-if="hasActionMessage"
      class="mw-diag-action-content mt-2"
      v-html="sanitize(actionMessage)"
    ></div>
    <div
      v-if="macroTitle"
      class="mw-diag-action-content mt-2 cursor d-flex"
      @click="copyMacroTitle"
    >
      <p class="mb-0" ref="macro-title">
        A cette étape, la macro
        <span class="macro-title">{{ macroTitle }}</span> s'enverra
      </p>
      <font-awesome-icon :icon="['fad', 'copy']" class="copy-icon" />
    </div>
    <div
      v-if="hasMacro && !macroTitle"
      class="mw-diag-action-content mt-2 cursor d-flex"
    >
      <font-awesome-icon :icon="['fal', 'spinner-third']" spin />
    </div>
    <!-- CHILDREN / STEPS -->
    <div v-if="diagToDisplay.children" class>
      <div v-for="(child, index) in diagToDisplay.children" :key="index">
        <base-button
          class="diag-step mt-2"
          outline
          type="primary"
          @click="routeToChild(child, $event)"
        >
          <font-awesome-icon
            v-if="child.redirection"
            :icon="['fa', 'external-link-square']"
          />
          {{ child.label }}
        </base-button>
      </div>
    </div>
    <base-button
      v-if="!isPreview"
      class="diag-step mt-2"
      outline
      type="primary"
      @click="routeToPreviousStep"
    >
      <font-awesome-icon :icon="['far', 'arrow-left']"></font-awesome-icon>
    </base-button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import NavigationBreadcrumb from '../NavigationBreadcrumb';
import NodeContentViewer from './NodeContentViewer';

import { sanitize } from '@/plugins/dompurify';

export default {
  name: 'diagnostic-view',
  components: {
    NavigationBreadcrumb,
    NodeContentViewer,
  },
  props: {
    isPreview: {
      type: Boolean,
      default: false,
    },
    previewDiag: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      macroTitle: null,
      diagToDisplay: {},
      sanitize: sanitize,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.getMacroTitle();
    });
  },
  methods: {
    async getMacroTitle() {
      this.macroTitle = null;
      if (
        !this.hasMacro ||
        !this.focusDiagItem.actions ||
        !this.focusDiagItem.actions.length
      )
        return;

      const macroId = this.focusDiagItem.actions[0].payload.macro_id;

      if (!macroId) return;
      const macro = await this.$services.zendesk.getMacroById(macroId);
      this.macroTitle = macro.title;
    },
    copyMacroTitle() {
      try {
        this.selectText(this.$refs['macro-title']);
        document.execCommand('copy');
        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        } else if (document.selection) {
          document.selection.empty();
        }
        this.$message({
          message: 'Titre de la macro copié avec succés !',
          type: 'success',
          center: true,
        });
      } catch (e) {
        this.$message({
          message: 'Une erreur est survenue, veuillez réessayer',
          type: 'error',
          center: true,
        });
      }
    },
    selectText(element) {
      let range;
      if (document.selection) {
        // IE
        range = document.body.createTextRange();
        range.moveToElementText(element);
        range.select();
      } else if (window.getSelection) {
        range = document.createRange();
        range.selectNode(element);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    },
    async routeToChild(child, e) {
      const { contentId } = this.$route.params;
      const { redirection, id } = child;

      const params = { query: {}, open: e.metaKey || e.ctrlKey };

      if (redirection) {
        const { entityId, entityType } = redirection;
        params.entityId = entityId;
        params.entityType = entityType;
        params.query.source = 'redirection';
      } else {
        // Is a Step
        params.entityId = id;
        params.entityType = 'Step';
        params.parentId = contentId;
        params.query.source = 'navigation';
      }

      this.goToWebTranslatedEntity(params);
    },
    routeToPreviousStep(e) {
      const { contentId } = this.$route.params;

      const params = {
        query: { source: 'navigation' },
        open: e.metaKey || e.ctrlKey,
      };

      // Is a Content or Step
      if (this.diagPath.length >= 2) {
        const prevStep = this.diagPath[this.diagPath.length - 2];
        params.entityType = prevStep.type;

        if (prevStep.type === 'Step') {
          params.entityId = prevStep.id;
          params.parentId = contentId;
        } else {
          params.entityId = contentId;
        }
      }
      // Is a Case
      else {
        params.entityId = this.navPath[this.navPath.length - 2].id;
        params.entityType = 'Case';
      }

      this.goToWebTranslatedEntity(params);
    },
    ...mapActions('webKnowledgeModule', ['goToWebTranslatedEntity']),
    ...mapActions(['createRedirectUrl']),
  },
  computed: {
    hasEmptyBody() {
      return (
        !this.diagToDisplay.body ||
        this.diagToDisplay.body === '<p></p>' ||
        this.diagToDisplay.body.match(/^<p data-uid="[^"]+"><\/p>$/)
      );
    },
    hasAction() {
      if (this.diagToDisplay) {
        return (
          this.diagToDisplay &&
          this.diagToDisplay.actions &&
          this.diagToDisplay.actions[0]
        );
      }
      return false;
    },
    hasMacro() {
      if (!this.hasAction) return false;

      const action = this.diagToDisplay.actions[0];

      return action.payload && action.payload.macro_id !== '';
    },
    hasActionMessage() {
      if (!this.hasAction) return false;

      const action = this.diagToDisplay.actions[0];

      return action.payload && action.payload.message !== '';
    },
    actionMessage() {
      return this.hasActionMessage
        ? this.diagToDisplay.actions[0].payload.message
        : '';
    },
    ...mapGetters('webKnowledgeModule', [
      'focusDiagItem',
      'diagPath',
      'navPath',
      'loading',
    ]),
  },
  watch: {
    focusDiagItem() {
      this.$nextTick(() => {
        this.getMacroTitle();
      });
    },
    isPreview: {
      handler() {
        this.diagToDisplay = this.isPreview
          ? this.previewDiag
          : this.focusDiagItem;
      },
      immediate: true,
    },
    loading: {
      handler() {
        this.diagToDisplay = this.isPreview
          ? this.previewDiag
          : this.focusDiagItem;
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.diag-step {
  border-radius: 2px;
}

.mw-diag-content {
  background-color: rgba($grey-1-mayday, 0.5);
  border-left: 3px solid #ced4da;
  padding: 16px;
  padding-bottom: 8px;
}

.preview {
  background-color: $grey-1-mayday;
}

.mw-diag-step-name {
  height: 32px;
}
.mw-divider {
  border-bottom: 1px solid $grey-4-mayday;
}
.mw-container {
  // padding-left: 56px;
  // padding-right: 56px;
}

.mw-diag-action-content {
  background: rgba($blue-mayday, 0.1);
  border-left: 3px solid $blue-mayday;
  padding: 16px;
  padding-bottom: 16px;
  &:hover {
    .copy-icon {
      visibility: visible;
    }
  }
}

.macro-title {
  font-weight: 900;
  padding: 0px 4px;
  &:hover {
    background: rgba($blue-mayday, 0.2);
  }
}

.copy-icon {
  visibility: hidden;
  margin-left: 8px;
  color: $grey-4-mayday;
  &:hover {
    color: $blue-mayday;
  }
}
</style>
